import { useFormInstance } from '@neo/blocks/forms/form-provider'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import { useGenericFormStore } from '@neo/blocks/forms/persistance-store'
import { Button, ButtonWithLoader } from '@neo/primitives/button'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Pen } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { FlowNodeTypes, type JsonValue } from '../../types'
import {
  type ApiRequestPayloadFormValues,
  createApiRequestPayloadFields,
} from './api-request-payload-form.config'
import { escapeDotsInKey, flattenPayloadFormat, unescapeDotsInKey } from './utils'

const FORM_PERSISTENCE_KEY = 'api-request-payload-form'

interface ApiRequestPayloadFormProps {
  onSubmit: (payload: Record<string, JsonValue>) => void
  onCancel: () => void
  isLoading: boolean
}

export const ApiRequestPayloadForm = ({
  onSubmit,
  onCancel,
  isLoading,
}: ApiRequestPayloadFormProps) => {
  const formId = 'api-request-payload-form'
  const { getNodes } = useTypedReactFlow()

  const [isJsonMode, setIsJsonMode] = useState(false)
  const [jsonValue, setJsonValue] = useState('')

  const formInstance = useFormInstance<ApiRequestPayloadFormValues>(formId)

  // Helper to read persisted form values from the GenericForm store
  const getPersistedFormValues = (): ApiRequestPayloadFormValues | null => {
    const stored = useGenericFormStore.getState().formValues[FORM_PERSISTENCE_KEY]
    return stored ? (stored as ApiRequestPayloadFormValues) : null
  }

  // Helper to persist form values to the GenericForm store
  const persistFormValues = (values: ApiRequestPayloadFormValues) => {
    useGenericFormStore.getState().setDefaultValues(FORM_PERSISTENCE_KEY, values)
  }

  const nodes = getNodes()
  const startNode = nodes.find((node) => node.type === FlowNodeTypes.start)

  let payloadFormat: Record<string, string | Record<string, unknown>> = {}

  if (startNode?.data?.config?.payload_format) {
    try {
      const parsedFormat =
        typeof startNode.data.config.payload_format === 'string'
          ? JSON.parse(startNode.data.config.payload_format)
          : startNode.data.config.payload_format || {}

      payloadFormat = parsedFormat
    } catch (error) {
      logger.warn('Error parsing payload format:', error)
    }
  }

  const flattenedFormat = useMemo(() => flattenPayloadFormat(payloadFormat), [payloadFormat])
  const keys = Object.keys(flattenedFormat)

  // Create default values based on field types
  const defaultValues: ApiRequestPayloadFormValues = {}
  Object.entries(flattenedFormat).forEach(([key, type]) => {
    switch (type) {
      case 'boolean':
        defaultValues[key] = false
        break
      case 'number':
        defaultValues[key] = 0
        break
      case 'list':
        defaultValues[key] = '[]'
        break
      case 'json':
        defaultValues[key] = '{}'
        break
      default:
        defaultValues[key] = ''
        break
    }
  })

  const fields = createApiRequestPayloadFields(payloadFormat)

  const processPayloadValue = (
    value: ApiRequestPayloadFormValues[keyof ApiRequestPayloadFormValues],
    fieldType?: string,
  ): JsonValue | undefined => {
    if (value === undefined) {
      return undefined
    }

    if (!fieldType) {
      return value !== '' ? (value as JsonValue) : undefined
    }

    switch (fieldType) {
      case 'boolean':
        return Boolean(value)
      case 'number':
        if (value !== 0 && value !== '') {
          return Number(value)
        }
        if (value === 0) {
          return 0
        }
        return undefined
      case 'list':
        if (typeof value === 'string' && value.trim() !== '') {
          try {
            const parsed = JSON.parse(value)
            if (Array.isArray(parsed)) {
              return parsed as JsonValue
            }
          } catch (error) {
            logger.warn('Error parsing list value:', error)
          }
        }
        return undefined
      case 'json':
        if (typeof value === 'string' && value.trim() !== '') {
          try {
            const parsed = JSON.parse(value)
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
              return parsed as JsonValue
            }
          } catch (error) {
            logger.warn('Error parsing JSON value:', error)
          }
        }
        return undefined
      default:
        if (value !== '') {
          return String(value)
        }
        return undefined
    }
  }

  const buildNestedPayload = (
    formValues: ApiRequestPayloadFormValues,
    format: Record<string, string>,
  ): Record<string, JsonValue> => {
    const payload: Record<string, JsonValue> = {}

    Object.entries(formValues).forEach(([key, value]) => {
      const fieldType = format[key]
      const processedValue = processPayloadValue(value, fieldType)

      if (processedValue === undefined) {
        return
      }

      if (!fieldType) {
        payload[key] = processedValue
        return
      }

      // Split on dots for nesting structure
      const pathKeys = key.split('.')
      let current = payload

      pathKeys.forEach((pathKey, index) => {
        // Unescape dots in the actual key name to restore original key
        const actualKey = unescapeDotsInKey(pathKey)

        if (index === pathKeys.length - 1) {
          current[actualKey] = processedValue
        } else {
          if (!current[actualKey] || typeof current[actualKey] !== 'object') {
            current[actualKey] = {}
          }
          current = current[actualKey] as Record<string, JsonValue>
        }
      })
    })

    return payload
  }

  // Convert form values to JSON string
  const convertFormValuesToJson = (formValues: ApiRequestPayloadFormValues): string => {
    const payload = buildNestedPayload(formValues, flattenedFormat)
    return JSON.stringify(payload, null, 2)
  }

  // Initialize JSON value from persisted form values when opening in JSON mode
  useEffect(() => {
    if (isJsonMode && !jsonValue) {
      const persistedValues = getPersistedFormValues()
      if (persistedValues && Object.keys(persistedValues).length > 0) {
        // Filter to only include keys present in current payload schema
        const filteredValues = Object.fromEntries(
          Object.entries(persistedValues).filter(([key]) => key in flattenedFormat),
        ) as ApiRequestPayloadFormValues
        if (Object.keys(filteredValues).length > 0) {
          setJsonValue(convertFormValuesToJson(filteredValues))
        } else {
          setJsonValue(convertFormValuesToJson(defaultValues))
        }
      } else {
        setJsonValue(convertFormValuesToJson(defaultValues))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally only run on mode change; other deps are stable during form lifetime
  }, [isJsonMode])

  const sanitizeFormValues = (values: Record<string, unknown>): ApiRequestPayloadFormValues => {
    const flattenValues = (
      input: Record<string, unknown>,
      prefix = '',
      result: ApiRequestPayloadFormValues = {},
    ): ApiRequestPayloadFormValues => {
      Object.entries(input).forEach(([key, value]) => {
        const newKey = prefix ? `${prefix}.${key}` : key

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          result[newKey] = value
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
          flattenValues(value as Record<string, unknown>, newKey, result)
        }
      })
      return result
    }

    const flatValues = flattenValues(values)

    const result: ApiRequestPayloadFormValues = {}
    Object.keys(flattenedFormat).forEach((key) => {
      const value = flatValues[key]
      if (value !== undefined) {
        result[key] = value
      }
    })
    return result
  }

  // Convert JSON object to form values (for JSON → key-value sync)
  const convertJsonToFormValues = (
    jsonObj: Record<string, unknown>,
  ): ApiRequestPayloadFormValues => {
    const result: ApiRequestPayloadFormValues = {}

    const flattenObject = (obj: Record<string, unknown>, prefix = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        // Escape dots in key names to match the form field keys
        const escapedKey = escapeDotsInKey(key)
        const fullKey = prefix ? `${prefix}.${escapedKey}` : escapedKey

        if (value === null || value === undefined) {
          return
        }

        if (Array.isArray(value)) {
          // Store arrays as JSON strings for list fields
          result[fullKey] = JSON.stringify(value)
        } else if (typeof value === 'object') {
          // Check if this key is expected to be a 'json' type field
          const fieldType = flattenedFormat[fullKey]
          if (fieldType === 'json') {
            result[fullKey] = JSON.stringify(value)
          } else {
            // Recurse into nested objects
            flattenObject(value as Record<string, unknown>, fullKey)
          }
        } else if (typeof value === 'boolean' || typeof value === 'number') {
          result[fullKey] = value
        } else {
          result[fullKey] = String(value)
        }
      })
    }

    flattenObject(jsonObj)
    return result
  }

  // Toggle between modes
  const handleModeToggle = () => {
    if (isJsonMode) {
      // Switching from JSON to form mode - populate form with JSON values and persist
      if (jsonValue.trim()) {
        try {
          const parsed = JSON.parse(jsonValue)
          if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            const formValues = convertJsonToFormValues(parsed)
            // Persist to form store and reset form to pick up new values
            persistFormValues(formValues)
            formInstance?.reset()
          } else {
            errorToast({
              title: 'Invalid JSON format',
              description: 'Payload must be a JSON object, not an array or primitive.',
            })
          }
        } catch {
          // Invalid JSON, show error and switch mode without populating
          errorToast({
            title: 'Invalid JSON format',
            description: 'Could not parse JSON. Form will use previous values.',
          })
        }
      }
      setIsJsonMode(false)
    } else {
      // Switching from form to JSON mode - convert form values to JSON
      const rawValues = (formInstance?.store.state.values || {}) as Record<string, unknown>
      const formValues = sanitizeFormValues(rawValues as ApiRequestPayloadFormValues)

      const hasValues = Object.values(formValues).some((value) => {
        if (typeof value === 'string') {
          const trimmed = value.trim()
          return trimmed !== '' && trimmed !== '0' && trimmed !== 'false'
        }
        if (typeof value === 'number') {
          return value !== 0
        }
        if (typeof value === 'boolean') {
          return value
        }
        return value !== undefined && value !== null
      })

      if (!hasValues || Object.keys(formValues).length === 0) {
        setJsonValue(convertFormValuesToJson(defaultValues))
      } else {
        setJsonValue(convertFormValuesToJson(formValues))
      }
      setIsJsonMode(true)
    }
  }

  const handleJsonSubmit = () => {
    try {
      const parsed = JSON.parse(jsonValue)
      // Validate that parsed value is a JSON object, not array or primitive
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        errorToast({
          title: 'Invalid JSON format',
          description: 'Payload must be a JSON object.',
        })
        return
      }
      // Persist to form cache so values are remembered
      const formValues = convertJsonToFormValues(parsed as Record<string, unknown>)
      persistFormValues(formValues)
      onSubmit(parsed as Record<string, JsonValue>)
    } catch (error) {
      logger.warn('handleJsonSubmit: Error parsing JSON:', error)
      errorToast({
        title: 'Invalid JSON format',
        description: 'Please check your JSON syntax and try again.',
      })
    }
  }

  const handleSubmit = (formValues: ApiRequestPayloadFormValues) => {
    // Rebuild nested structure from flattened dot-notation keys
    const filteredValues = sanitizeFormValues(formValues)
    const payload = buildNestedPayload(filteredValues, flattenedFormat)
    onSubmit(payload)
  }

  // If no payload keys, show a message
  if (keys.length === 0) {
    return (
      <div className="space-y-4 p-4 w-72">
        <div>
          <h3 className="text-h4-500 text-foreground">Test agent</h3>
          <p className="text-xs-400 text-secondary mt-1">No payload parameters required</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1"
            data-testid="button-cancel-payload"
          >
            Cancel
          </Button>
          <ButtonWithLoader
            variant="default"
            size="sm"
            onClick={() => onSubmit({})}
            loading={isLoading}
            className="flex-1"
            data-testid="button-submit-payload"
          >
            {isLoading ? 'Starting...' : 'Start test'}
          </ButtonWithLoader>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-0 w-80">
      <div className="p-4 pb-2 border-b-st2 border-b">
        <h3 className="text-h4-500 text-foreground">Test agent</h3>
        <p className="text-xs-400 text-secondary mt-1">API payload values</p>
      </div>

      <ScrollArea className="flex max-h-[400px] flex-col overflow-y-auto">
        <div className="p-4 pb-1">
          {isJsonMode ? (
            <div className="border rounded-md p-2 bg-bg2">
              <CodeEditor
                language="json"
                value={jsonValue}
                onChange={setJsonValue}
                className="min-h-[200px]"
                data-testid="code-editor-payload-json"
              />
            </div>
          ) : (
            <GenericForm<ApiRequestPayloadFormValues>
              id={formId}
              fields={fields}
              className="gap-4"
              onSubmit={handleSubmit}
              defaultValues={defaultValues}
              includeSubmitButton={false}
              persistanceKey="api-request-payload-form"
            />
          )}
        </div>
      </ScrollArea>

      <div className="px-4 pb-2">
        <Button
          variant="ghost"
          onClick={handleModeToggle}
          type="button"
          size="sm"
          data-testid="button-toggle-json-mode"
        >
          <Pen className="h-4 w-4 mr-2" /> {isJsonMode ? 'Key-Value' : 'JSON'}
        </Button>
      </div>

      <div className="flex gap-2 px-4 pb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1"
          type="button"
          data-testid="button-cancel-payload"
        >
          Cancel
        </Button>
        <ButtonWithLoader
          variant="default"
          size="sm"
          form={isJsonMode ? undefined : 'api-request-payload-form'}
          type={isJsonMode ? 'button' : 'submit'}
          onClick={isJsonMode ? handleJsonSubmit : undefined}
          loading={isLoading}
          className="flex-1"
          data-testid="button-submit-payload"
        >
          {isLoading ? 'Starting...' : 'Start test'}
        </ButtonWithLoader>
      </div>
    </div>
  )
}
